// src/pages/InstagramPage.js
// S&I. Instagram Post Generator — SuperAdmin Integration
// Theme-spezifische Posts mit KI-Textvorschlägen, Bild-Upload, Live Preview
import React, { useState, useRef, useCallback } from 'react';
import PostCanvas from '../lib/postCanvas';
import styled, { css } from 'styled-components';
import { THEMES } from '../lib/reelThemes';
import { adminFetch } from '../lib/apiClient';
import PinterestPublish from '../components/PinterestPublish';

// ============================================
// DESIGN TOKENS
// ============================================
const colors = { black: '#0A0A0A', white: '#FAFAFA', red: '#C41E3A', gray: '#666666', lightGray: '#E5E5E5', background: '#F5F5F5' };

const LAYOUTS = {
  statement: { name: 'Statement', desc: 'Große Headline, minimal' },
  split: { name: 'Split', desc: 'Bild links, Text rechts' },
  list: { name: 'Liste', desc: 'Strukturierte Items' },
  dark: { name: 'Dark', desc: 'Invertiert, Akzente' },
  fullbleed: { name: 'Fullbleed', desc: 'Bild-Hintergrund' },
};

const CATEGORIES = [
  { id: 'vorstellung', label: 'Vorstellung', icon: '👋', desc: 'Wer sind wir' },
  { id: 'features', label: 'Features', icon: '⚡', desc: 'Was bieten wir' },
  { id: 'themes', label: 'Themes', icon: '🎨', desc: 'Theme-Vorstellung' },
  { id: 'dashboard', label: 'Dashboard', icon: '📊', desc: 'Admin-Features' },
  { id: 'pricing', label: 'Pricing', icon: '💰', desc: 'Preise & Pakete' },
  { id: 'cta', label: 'Call to Action', icon: '🚀', desc: 'Kontakt & Demo' },
  { id: 'behind', label: 'Behind the Scenes', icon: '📸', desc: 'Einblicke' },
  { id: 'tipps', label: 'Hochzeitstipps', icon: '💡', desc: 'Mehrwert-Content' },
  { id: 'custom', label: 'Eigenes Thema', icon: '✏️', desc: 'Freitext-Eingabe' },
];

const HOOK_TRIGGERS = [
  { id: 'auto', label: 'Auto' },
  { id: 'surprise', label: 'Überraschung' },
  { id: 'fear', label: 'Angst/Scham' },
  { id: 'ego', label: 'Ego/Geschmack' },
  { id: 'urgency', label: 'Dringlichkeit' },
  { id: 'desire', label: 'Verlangen' },
];

// ============================================
// STYLED COMPONENTS (SuperAdmin Editorial Style)
// ============================================
const PageHeader = styled.div`
  margin-bottom: 2rem;
  h1 {
    font-family: 'Oswald', sans-serif;
    font-size: 2rem;
    font-weight: 700;
    text-transform: uppercase;
    color: ${colors.black};
    margin-bottom: 0.25rem;
  }
  p {
    font-family: 'Source Serif 4', serif;
    font-style: italic;
    color: ${colors.gray};
    font-size: 1rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 2rem;
  align-items: start;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid ${colors.lightGray};
  padding: 1.5rem;
`;

const SectionLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${colors.gray};
  margin-bottom: 0.75rem;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 1.25rem;
`;

const Chip = styled.button`
  font-family: 'Inter', sans-serif;
  font-size: 0.7rem;
  font-weight: ${p => p.$active ? 600 : 400};
  padding: 0.5rem 0.85rem;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid ${p => p.$active ? colors.black : colors.lightGray};
  background: ${p => p.$active ? colors.black : '#fff'};
  color: ${p => p.$active ? '#fff' : colors.black};
  &:hover { border-color: ${colors.black}; }
`;

const TabBar = styled.div`
  display: flex;
  border-bottom: 2px solid ${colors.black};
  margin-bottom: 1.25rem;
`;

const Tab = styled.button`
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  font-weight: ${p => p.$active ? 600 : 400};
  padding: 0.75rem 1.25rem;
  cursor: pointer;
  border: none;
  background: ${p => p.$active ? colors.black : 'transparent'};
  color: ${p => p.$active ? '#fff' : colors.black};
  transition: all 0.15s;
`;

const Label = styled.label`
  font-family: 'Inter', sans-serif;
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${colors.gray};
  display: block;
  margin-bottom: 0.25rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.65rem 0.75rem;
  border: 1px solid ${colors.lightGray};
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  background: #fff;
  outline: none;
  transition: border 0.15s;
  &:focus { border-color: ${colors.red}; }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.65rem 0.75rem;
  border: 1px solid ${colors.lightGray};
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  background: #fff;
  resize: vertical;
  outline: none;
  &:focus { border-color: ${colors.red}; }
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const Field = styled.div`margin-bottom: 0.75rem;`;

const PrimaryButton = styled.button`
  font-family: 'Oswald', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0.85rem 2rem;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  background: ${p => p.$loading ? colors.gray : colors.red};
  color: #fff;
  pointer-events: ${p => p.$loading ? 'none' : 'auto'};
  &:hover { background: #a01830; }
`;

const SecondaryButton = styled.button`
  font-family: 'Oswald', sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  border: 2px solid ${colors.black};
  background: transparent;
  color: ${colors.black};
  transition: all 0.2s;
  &:hover { background: ${colors.black}; color: #fff; }
`;

const FileLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid ${colors.lightGray};
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  transition: all 0.15s;
  &:hover { border-color: ${colors.black}; }
  input { display: none; }
`;

const ImagePreview = styled.img`
  width: 36px;
  height: 36px;
  object-fit: cover;
  border: 1px solid ${colors.lightGray};
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${colors.red};
  font-size: 0.8rem;
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.35rem;
  margin-bottom: 1rem;
`;

const CategoryBtn = styled.button`
  font-family: 'Inter', sans-serif;
  font-size: 0.65rem;
  padding: 0.65rem 0.5rem;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
  border: 1px solid ${p => p.$active ? colors.black : colors.lightGray};
  background: ${p => p.$active ? colors.black : '#fff'};
  color: ${p => p.$active ? '#fff' : colors.black};
  &:hover { border-color: ${colors.black}; }
  span.icon { font-size: 1rem; margin-right: 0.25rem; }
  span.desc { display: block; font-size: 0.5rem; opacity: 0.5; margin-top: 2px; }
`;

const SuggestionCard = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1px solid ${colors.lightGray};
  padding: 1rem;
  cursor: pointer;
  transition: all 0.15s;
  margin-bottom: 0.5rem;
  &:hover { border-color: ${colors.red}; box-shadow: 0 2px 12px rgba(0,0,0,0.04); }
`;

const SuggestionEyebrow = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.55rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${colors.red};
  margin-bottom: 0.25rem;
`;

const SuggestionHeadline = styled.div`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.1rem;
  font-weight: 300;
  color: ${colors.black};
  margin-bottom: 0.25rem;
`;

const SuggestionBody = styled.div`
  font-size: 0.7rem;
  color: ${colors.gray};
  line-height: 1.4;
`;

const ApplyLabel = styled.div`
  font-size: 0.55rem;
  color: ${colors.red};
  font-weight: 600;
  margin-top: 0.4rem;
`;

const SuggestionCaption = styled.div`
  font-size: 0.6rem;
  color: ${colors.gray};
  margin-top: 0.35rem;
  padding-top: 0.35rem;
  border-top: 1px solid ${colors.lightGray};
  line-height: 1.4;
`;

const SuggestionHashtags = styled.div`
  font-size: 0.55rem;
  color: #3B82F6;
  margin-top: 0.2rem;
`;

const CaptionBox = styled.div`
  background: #F9F9F7;
  border: 1px solid ${colors.lightGray};
  padding: 1rem;
  margin-top: 0.25rem;
`;

const CopyButton = styled.button`
  font-family: 'Oswald', sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.6rem 1.25rem;
  margin-top: 0.5rem;
  cursor: pointer;
  border: 2px solid ${colors.red};
  background: transparent;
  color: ${colors.red};
  transition: all 0.2s;
  &:hover { background: ${colors.red}; color: #fff; }
`;

const ActionBar = styled.div`
  margin-top: 0.75rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const InstagramButton = styled.button`
  font-family: 'Oswald', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0.85rem 2rem;
  cursor: pointer;
  border: none;
  background: linear-gradient(135deg, #833AB4, #E1306C, #F77737);
  color: #fff;
  transition: all 0.2s;
  &:hover { opacity: 0.9; transform: translateY(-1px); }
`;

const IgReadyBox = styled.div`
  margin-top: 0.75rem;
  background: #fff;
  border: 2px solid #10B981;
  padding: 1.25rem;
`;

const IgReadySteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const IgStep = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  color: ${p => p.$done ? '#10B981' : colors.black};
  font-weight: ${p => p.$done ? 500 : 600};
`;

const IgOpenLink = styled.a`
  display: inline-block;
  font-family: 'Oswald', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0.75rem 2rem;
  background: linear-gradient(135deg, #833AB4, #E1306C, #F77737);
  color: #fff;
  text-decoration: none;
  transition: all 0.2s;
  &:hover { opacity: 0.9; }
`;

const PreviewSticky = styled.div`
  position: sticky;
  top: 80px;
  @media (max-width: 1024px) {
    position: static;
    order: -1;
  }
`;

const PreviewLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.6rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${colors.gray};
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const Hint = styled.p`
  font-size: 0.65rem;
  color: ${colors.gray};
  margin-top: 0.5rem;
`;

// ============================================
// MAIN COMPONENT
// ============================================
export default function InstagramPage({ platform = 'instagram', seed = null }) {
  const isPinterest = platform === 'pinterest';
  const platformName = isPinterest ? 'Pinterest' : 'Instagram';
  const ASPECT = isPinterest
    ? { W: 360, H: 540, label: '1080×1620', ratio: '2:3' }
    : { W: 360, H: 450, label: '1080×1350', ratio: '4:5' };
  const [theme, setTheme] = useState(seed?.theme || 'classic');
  const [layout, setLayout] = useState(seed?.layout || 'statement');
  const [eyebrow, setEyebrow] = useState(seed?.eyebrow ?? 'Premium Hochzeitswebsites');
  const [headline, setHeadline] = useState(seed?.headline ?? 'Eure Hochzeit verdient mehr als ein Template.');
  const [accentWord, setAccentWord] = useState(seed?.accentWord ?? 'Template.');
  const [bodyText, setBodyText] = useState(seed?.body ?? 'Handgemacht in Hamburg. Mit Liebe zum Detail.');
  const [image, setImage] = useState(seed?.image || null);
  const [pageNum, setPageNum] = useState('');
  const [tab, setTab] = useState('edit');
  const [aiCategory, setAiCategory] = useState('vorstellung');
  const [customPrompt, setCustomPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [caption, setCaption] = useState(seed?.caption || '');
  const [captionCopied, setCaptionCopied] = useState(false);
  const [igReady, setIgReady] = useState(false);
  const [hookTrigger, setHookTrigger] = useState('auto');
  const [useTrends, setUseTrends] = useState(true);
  const postRef = useRef(null);

  const t = THEMES[theme];
  const isDark = layout === 'dark' || layout === 'fullbleed' || t.alwaysDark;
  const bg = isDark ? (t.bgDark || t.bg) : t.bg;
  const textColor = isDark ? (t.textDark || t.text) : t.text;
  const logoSt = isDark ? t.logoDarkStyle : t.logoStyle;

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setImage(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  // ==========================================
  // AI TEXT GENERATION
  // ==========================================
  const generateSuggestions = async () => {
    setAiLoading(true);
    setSuggestions([]);
    const cat = CATEGORIES.find(c => c.id === aiCategory);
    const topicContext = aiCategory === 'custom' ? customPrompt : cat.desc;

    const triggerMap = {
      surprise: 'Überraschung — eine kontraintuitive Aussage oder Statistik, die sofort eine Neugierlücke öffnet.',
      fear: 'Angst/Scham — soziales Urteil ("eure Gäste merken es", "das sieht man sofort"), nie gemein.',
      ego: 'Ego/Geschmack — schmeichelt der In-Group ("wer Geschmack hat…") und fordert Widerspruch heraus.',
      urgency: 'Dringlichkeit — unterbricht eine Handlung ("schickt die Einladungen noch nicht") mit Deadline + Verlust.',
      desire: 'Verlangen — aspirativer Reveal + Social Proof ("100 Gäste haben es gespeichert, bevor sie zugesagt haben").',
    };
    const triggerLine = (hookTrigger !== 'auto' && triggerMap[hookTrigger])
      ? `Primärer Hook-Trigger: ${triggerMap[hookTrigger]}`
      : 'Wähle pro Vorschlag den stärksten Hook-Trigger (Überraschung, Angst/Scham, Ego, Dringlichkeit oder Verlangen) und variiere ihn über die 3 Vorschläge.';

    const brand = `Du arbeitest für S&I. Wedding (sarahiver.com) — ein Premium-Hochzeitswebsite-Service aus Hamburg von Sarah & Iver.
Kontext: handgemachte Hochzeitswebsites mit eigener Domain. Themes: Classic, Editorial, Botanical, Contemporary, Luxe, Neon, Video. Features: RSVP, Gästeliste, Love Story, Countdown, Foto-Upload, Musik-Wünsche, Passwortschutz, Admin-Dashboard. Zielgruppe: verlobte Paare mit Designanspruch, 25–40, DACH-Raum. Tonalität: warm aber selbstbewusst, leicht editorial, nie billig oder kitschig.
Nische-Realität: fast aller virale Wedding-Website-Content ist US/englisch (Zola, The Knot, Joy). Deutschsprachiger Content ist nahezu unbesetztes Whitespace — das ist der strategische Vorteil.`;

    let platformBlock, schema, searchHint;
    if (isPinterest) {
      platformBlock = `Plattform: PINTEREST (2:3 Pin). Pinterest ist eine SUCHMASCHINE, kein Feed.
- Weniger Aggression, mehr Aspiration + Nutzwert; evergreen statt Trend-Hetze.
- "headline" = keyword-reiches, suchbares Versprechen (z.B. "Hochzeitswebsite Ideen", "Save-the-Date Wording").
- "caption" = SEO-Beschreibung (2–3 Sätze, keyword-reich, endet mit sanftem CTA).
- "hashtags" = 4–6 Pinterest-Keywords/Begriffe, die Paare wirklich in die Suche eingeben (Komma-getrennt).
Optimiere für Speichern (Saves), Klicks und Keyword-Ranking.`;
      schema = `[{"eyebrow":"...","headline":"keyword-reiches Versprechen","accentWord":"ein Wort aus headline","body":"1–2 Sätze Nutzwert","caption":"SEO-Beschreibung mit Keywords","hashtags":"keyword1, keyword2, keyword3"}]  (genau 3 Objekte im Array)`;
      searchHint = 'Nutze web_search um aktuell gut rankende Pinterest-Keywords rund um Hochzeitswebsites zu finden (suche "wedding website ideas pinterest", "hochzeitswebsite ideen pinterest"). Verwende die stärksten Such-Keywords in den Vorschlägen.';
    } else {
      platformBlock = `Plattform: INSTAGRAM (Reel/Post, 4:5). Ziel: Scroll-Stopp in den ersten 2 Sekunden; optimiert auf Saves, Sends und Kommentare.
- "headline" = aggressiver, scroll-stoppender Hook, der den Betrachter direkt anspricht. Neugierlücke öffnen, Auflösung zurückhalten.
- "caption" = 3–5 Sätze, warm aber mit Sog, endet mit Kommentar-Bait + dezentem CTA (z.B. "Link in Bio").
- "hashtags" = genau 5 Hashtags mit hoher Reichweite für Hochzeitswebsites.
${triggerLine}`;
      schema = `[{"eyebrow":"...","headline":"scroll-stoppender Hook","accentWord":"ein Wort aus headline","body":"1–2 Sätze, max 25 Wörter","caption":"...","hashtags":"#tag1 #tag2 #tag3 #tag4 #tag5"}]  (genau 3 Objekte im Array)`;
      searchHint = 'Nutze web_search um aktuelle, trendende Hochzeits-Hashtags auf Instagram zu finden (suche "trending wedding hashtags 2026 Instagram deutsch"). Verwende die gefundenen Hashtags.';
    }

    const prompt = `${brand}

${platformBlock}

Theme: "${THEMES[theme].name}" — Layout: "${LAYOUTS[layout].name}"
Kategorie: "${cat.label}" — ${topicContext}
${layout === 'list' ? "List-Layout: body = 5–7 Items, eins pro Zeile, Format 'Titel|Beschreibung'." : ''}

Regeln:
- accentWord muss WORTWÖRTLICH in headline vorkommen.
- ${useTrends ? 'Beziehe aktuelle Trends ein (über die Web-Suche).' : 'Web-Suche ist deaktiviert — nutze dein Wissen.'}

Antworte NUR mit validem JSON Array, kein Markdown:
${schema}`;

    try {
      const response = await adminFetch('/api/ai-suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, searchHint, webSearch: useTrends }),
      });
      const data = await response.json();
      // Extract text from all content blocks (may include tool_use, tool_result, text)
      const text = (data.content || [])
        .filter(i => i.type === 'text')
        .map(i => i.text || '')
        .join('\n');
      const clean = text.replace(/```json|```/g, '').trim();
      // Find the JSON array in the response
      const jsonMatch = clean.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        setSuggestions(JSON.parse(jsonMatch[0]));
      } else {
        throw new Error('No JSON found in response');
      }
    } catch (err) {
      console.error(err);
      setSuggestions([{ eyebrow: 'Fehler', headline: 'API nicht erreichbar', accentWord: 'erreichbar', body: 'Bitte nochmal versuchen.' }]);
    }
    setAiLoading(false);
  };

  const applySuggestion = (s) => {
    setEyebrow(s.eyebrow);
    setHeadline(s.headline);
    setAccentWord(s.accentWord);
    setBodyText(s.body);
    setCaption(s.caption + '\n\n' + (s.hashtags || ''));
    setTab('edit');
  };

  const copyCaption = () => {
    navigator.clipboard.writeText(caption).then(() => {
      setCaptionCopied(true);
      setTimeout(() => setCaptionCopied(false), 2000);
    });
  };

  const prepareForInstagram = async () => {
    // 1. Download PNG (saves to gallery on mobile)
    await downloadPNG();
    // 2. Copy caption to clipboard
    if (caption) {
      await navigator.clipboard.writeText(caption);
    }
    // 3. Show ready state
    setIgReady(true);
    setTimeout(() => setIgReady(false), 30000);
  };

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const igLink = isPinterest
    ? 'https://www.pinterest.com/'
    : (isMobile ? 'instagram://camera' : 'https://www.instagram.com/');

  // ==========================================
  // DOWNLOAD
  // ==========================================
  const renderToDataUrl = useCallback(async () => {
    const el = postRef.current;
    if (!el) return null;

    // Load html2canvas if needed
    if (!window.html2canvas) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    try {
      // Create a full-size (1080×1350) offscreen clone for sharp rendering
      const clone = el.cloneNode(true);

      // html2canvas 1.4.1 verrechnet sich beim Vermessen von Textknoten, die
      // Zeichen außerhalb der Standardebene enthalten — Emojis belegen intern
      // zwei Einheiten, die Bibliothek zählt aber eine. Ergebnis:
      //   IndexSizeError: offset 56 is larger than the node's length (55)
      // Deshalb werden solche Zeichen im KLON entfernt. Das Original im
      // Editor bleibt unberührt, der Nutzer sieht seinen Text weiterhin.
      const stripAstral = (node) => {
        if (node.nodeType === 3) {
          // Nur Zeichen ab U+10000 (die als Surrogatpaar gespeichert werden)
          // plus Variantenselektoren und Zero-Width-Joiner. Symbole aus der
          // Standardebene wie ✓ · — lösen den Fehler nicht aus und bleiben.
          const cleaned = node.nodeValue
            .replace(/[\u{10000}-\u{10FFFF}\u{FE00}-\u{FE0F}\u{200D}]/gu, '')
            .replace(/\s{2,}/g, ' ')
            .trim();
          if (cleaned !== node.nodeValue) node.nodeValue = cleaned;
          return;
        }
        Array.from(node.childNodes || []).forEach(stripAstral);
      };
      stripAstral(clone);
      clone.style.width = (ASPECT.W * 3) + 'px';
      clone.style.height = (ASPECT.H * 3) + 'px';
      clone.style.transform = 'scale(1)';
      clone.style.position = 'fixed';
      clone.style.left = '-9999px';
      clone.style.top = '0';
      clone.style.zIndex = '-1';

      // Scale all inner styles by 3x
      const scaleInner = (node) => {
        if (node.style) {
          const fs = node.style.fontSize;
          if (fs && fs.includes('rem')) {
            const val = parseFloat(fs);
            node.style.fontSize = (val * 3) + 'rem';
          }
          // Scale padding, margin, gap, width, height in px
          ['padding', 'paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight',
           'margin', 'marginTop', 'marginBottom', 'marginLeft', 'marginRight',
           'top', 'left', 'right', 'bottom', 'gap', 'borderRadius',
          ].forEach(prop => {
            const v = node.style[prop];
            if (v && v.includes('px') && !v.includes('calc')) {
              node.style[prop] = (parseFloat(v) * 3) + 'px';
            }
          });
          // Scale fixed dimensions
          ['width', 'height'].forEach(prop => {
            const v = node.style[prop];
            if (v && v.includes('px') && !v.includes('calc') && !v.includes('100')) {
              node.style[prop] = (parseFloat(v) * 3) + 'px';
            }
          });
        }
        Array.from(node.children || []).forEach(scaleInner);
      };
      // Don't scale inner — let html2canvas handle it with scale:3
      
      document.body.appendChild(clone);

      const canvas = await window.html2canvas(clone, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        width: ASPECT.W,
        height: ASPECT.H,
        imageTimeout: 0,
        logging: false,
      });

      document.body.removeChild(clone);

      return canvas.toDataURL('image/png', 1.0);
    } catch (err) {
      console.error('Render error:', err);
      alert('Nutze Screenshot als Alternative: Mac ⌘+Shift+4 / Win Win+Shift+S');
      return null;
    }
  }, [ASPECT.W, ASPECT.H]);

  const downloadPNG = useCallback(async () => {
    const dataUrl = await renderToDataUrl();
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.download = `si-${platform}-${theme}-${layout}.png`;
    a.href = dataUrl;
    a.click();
  }, [renderToDataUrl, platform, theme, layout]);

  // Für Pinterest-API: reines Base64 ohne DataURL-Prefix
  const getImageBase64 = useCallback(async () => {
    const dataUrl = await renderToDataUrl();
    if (!dataUrl) throw new Error('Bild konnte nicht gerendert werden');
    return dataUrl.split(',')[1];
  }, [renderToDataUrl]);

  // Artikel-Seed aus dem Publish-Panel: füttert die KI mit Titel + Beschreibung
  const seedFromArticle = useCallback((meta) => {
    setAiCategory('custom');
    setCustomPrompt(
      `Erstelle Pins, die auf diesen Blog-Artikel neugierig machen (der Pin verlinkt auf den Artikel):\n` +
      `Titel: ${meta.title}\n` +
      `Beschreibung: ${meta.description}\n` +
      `Der Pin soll das Kernversprechen des Artikels tragen — suchbar, evergreen, zum Speichern.`
    );
    setTab('ai');
  }, []);

  // ==========================================
  // RENDER HEADLINE WITH ACCENT
  // ==========================================
  // Der Renderer liegt jetzt in lib/postCanvas.js — dieselbe Komponente
  // erzeugt auch die Pinterest-Pins, damit beide identisch aussehen.
  const W = ASPECT.W, H = ASPECT.H;
  const renderPost = () => (
    <PostCanvas
      theme={theme}
      layout={layout}
      W={W}
      H={H}
      eyebrow={eyebrow}
      headline={headline}
      accentWord={accentWord}
      bodyText={bodyText}
      image={image}
      pageNum={pageNum}
    />
  );

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <>
      <Grid>
        {/* LEFT: Controls */}
        <div>
          {/* Theme + Layout Selection */}
          <Panel>
            <SectionLabel>Theme</SectionLabel>
            <ChipRow>
              {Object.entries(THEMES).map(([id, th]) => (
                <Chip key={id} $active={theme === id} onClick={() => setTheme(id)}>{th.name}</Chip>
              ))}
            </ChipRow>

            <SectionLabel>Layout</SectionLabel>
            <ChipRow>
              {Object.entries(LAYOUTS).map(([id, l]) => (
                <Chip key={id} $active={layout === id} onClick={() => setLayout(id)}>
                  {l.name}
                </Chip>
              ))}
            </ChipRow>
          </Panel>

          {/* Tabs: Edit / AI */}
          <Panel style={{ marginTop: '0.75rem' }}>
            <TabBar>
              <Tab $active={tab === 'edit'} onClick={() => setTab('edit')}>✏️ Text bearbeiten</Tab>
              <Tab $active={tab === 'ai'} onClick={() => setTab('ai')}>🤖 KI-Textvorschläge</Tab>
            </TabBar>

            {tab === 'edit' ? (
              <>
                <FieldRow>
                  <Field><Label>Eyebrow</Label><Input value={eyebrow} onChange={e => setEyebrow(e.target.value)} /></Field>
                  <Field><Label>Akzent-Wort</Label><Input value={accentWord} onChange={e => setAccentWord(e.target.value)} /></Field>
                </FieldRow>
                <Field><Label>Headline</Label><Textarea value={headline} onChange={e => setHeadline(e.target.value)} rows={2} /></Field>
                <Field>
                  <Label>Body {layout === 'list' && '(Zeile pro Item, | für Beschreibung)'}</Label>
                  <Textarea value={bodyText} onChange={e => setBodyText(e.target.value)} rows={layout === 'list' ? 6 : 2} />
                </Field>
                <Field>
                  <Label>Bild</Label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <FileLabel>
                      📷 Bild wählen...
                      <input type="file" accept="image/*" onChange={handleImage} />
                    </FileLabel>
                    {image && <>
                      <ImagePreview src={image} alt="" />
                      <RemoveBtn onClick={() => setImage(null)}>✕</RemoveBtn>
                    </>}
                  </div>
                </Field>
                <Field>
                  <Label>Seitenzahl</Label>
                  <Input value={pageNum} onChange={e => setPageNum(e.target.value)} placeholder="z.B. 1/4" style={{ maxWidth: 120 }} />
                </Field>
                {caption && (
                  <CaptionBox>
                    <Label>{isPinterest ? 'Beschreibung + Keywords' : 'Caption + Hashtags'}</Label>
                    <Textarea value={caption} onChange={e => setCaption(e.target.value)} rows={8} style={{ fontSize: '0.8rem', lineHeight: 1.6 }} />
                    <CopyButton onClick={copyCaption}>
                      {captionCopied ? '✓ Kopiert!' : '📋 Caption kopieren'}
                    </CopyButton>
                  </CaptionBox>
                )}
              </>
            ) : (
              <>
                <SectionLabel>Kategorie</SectionLabel>
                <CategoryGrid>
                  {CATEGORIES.map(cat => (
                    <CategoryBtn key={cat.id} $active={aiCategory === cat.id} onClick={() => setAiCategory(cat.id)}>
                      <span className="icon">{cat.icon}</span> {cat.label}
                      <span className="desc">{cat.desc}</span>
                    </CategoryBtn>
                  ))}
                </CategoryGrid>

                {aiCategory === 'custom' && (
                  <Field><Label>Thema beschreiben</Label>
                    <Textarea value={customPrompt} onChange={e => setCustomPrompt(e.target.value)} rows={2} placeholder="z.B. Warum Passwortschutz wichtig ist..." />
                  </Field>
                )}

                {!isPinterest && (
                  <>
                    <SectionLabel>Hook-Stil</SectionLabel>
                    <ChipRow>
                      {HOOK_TRIGGERS.map(h => (
                        <Chip key={h.id} $active={hookTrigger === h.id} onClick={() => setHookTrigger(h.id)}>{h.label}</Chip>
                      ))}
                    </ChipRow>
                  </>
                )}

                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: colors.gray, margin: '0 0 1rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={useTrends} onChange={e => setUseTrends(e.target.checked)} />
                  Aktuelle Trends einbeziehen (Web-Suche)
                </label>

                <PrimaryButton $loading={aiLoading} onClick={generateSuggestions}>
                  {aiLoading ? <><Spinner /> Generiere...</> : '🤖 3 Vorschläge generieren'}
                </PrimaryButton>

                {suggestions.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <SectionLabel>Klick zum Übernehmen</SectionLabel>
                    {suggestions.map((s, i) => (
                      <SuggestionCard key={i} onClick={() => applySuggestion(s)}>
                        <SuggestionEyebrow>{s.eyebrow}</SuggestionEyebrow>
                        <SuggestionHeadline>
                          {s.headline.includes(s.accentWord) ? (
                            <>{s.headline.split(s.accentWord)[0]}<span style={{ color: colors.red, fontStyle: 'italic' }}>{s.accentWord}</span>{s.headline.split(s.accentWord).slice(1).join(s.accentWord)}</>
                          ) : s.headline}
                        </SuggestionHeadline>
                        <SuggestionBody>{s.body.length > 100 ? s.body.substring(0, 100) + '…' : s.body}</SuggestionBody>
                        {s.caption && <SuggestionCaption>📝 {s.caption.length > 80 ? s.caption.substring(0, 80) + '…' : s.caption}</SuggestionCaption>}
                        {s.hashtags && <SuggestionHashtags>{s.hashtags.substring(0, 60)}…</SuggestionHashtags>}
                        <ApplyLabel>→ Übernehmen (Post + Caption + Hashtags)</ApplyLabel>
                      </SuggestionCard>
                    ))}
                  </div>
                )}
              </>
            )}
          </Panel>

          {isPinterest && (
            <PinterestPublish
              getImageBase64={getImageBase64}
              title={headline}
              description={caption}
              onSeedArticle={seedFromArticle}
              meta={{ theme, layout, headline, accentWord, eyebrow, source: 'editor' }}
            />
          )}

          {/* Download + Instagram */}
          <ActionBar>
            <SecondaryButton onClick={downloadPNG}>⬇ PNG Download ({ASPECT.label})</SecondaryButton>
            <InstagramButton onClick={prepareForInstagram}>
              📱 Für {platformName} vorbereiten
            </InstagramButton>
          </ActionBar>
          {igReady && (
            <IgReadyBox>
              <IgReadySteps>
                <IgStep $done={true}>✅ Bild heruntergeladen {isMobile ? '(in Fotos/Galerie)' : ''}</IgStep>
                <IgStep $done={true}>✅ Caption in Zwischenablage kopiert</IgStep>
                <IgStep $done={false}>{isMobile
                  ? `📱 ${platformName} öffnen → + → ${isPinterest ? 'Pin erstellen' : 'Post'} → Bild aus Galerie wählen → Text einfügen (lange drücken → Einsetzen)`
                  : `📱 ${platformName} öffnen → + → ${isPinterest ? 'Pin erstellen' : 'Post'} → Bild einfügen → Text einfügen (Strg+V)`
                }</IgStep>
              </IgReadySteps>
              <IgOpenLink href={igLink} target="_blank" rel="noopener noreferrer">
                {platformName} öffnen →
              </IgOpenLink>
            </IgReadyBox>
          )}
          <Hint>Tipp: Bei Bildern im Post ggf. Screenshot als Alternative (⌘+Shift+4 / Win+Shift+S)</Hint>
        </div>

        {/* RIGHT: Preview */}
        <PreviewSticky>
          <PreviewLabel>Live Preview</PreviewLabel>
          <div ref={postRef} style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
            {renderPost()}
          </div>
        </PreviewSticky>
      </Grid>
    </>
  );
}
